import { useState } from "react";
import { useWorkerI9, useEVerifyCases } from "@/hooks/useWorkerI9";
import { ManualI9ImportDialog } from "@/components/controller/ManualI9ImportDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";
import { Shield, FileText, CheckCircle2, AlertTriangle, Clock, Plus } from "lucide-react";

interface Props {
  vendorId: string;
  vendorName: string;
  workerId?: string;
  companyId: string;
  w9OnFile?: boolean;
  w9Date?: string | null;
  agreementSigned?: string | null;
  is1099Eligible?: boolean;
  paidYTD?: number;
  auditLog?: Array<{ created_at: string; field_name: string; change_reason: string | null; changed_by: string | null }>;
}

const STATUS_ICON: Record<string, React.ReactNode> = {
  complete: <CheckCircle2 className="h-4 w-4 text-primary" />,
  authorized: <CheckCircle2 className="h-4 w-4 text-primary" />,
  incomplete: <AlertTriangle className="h-4 w-4 text-warning" />,
  pending: <Clock className="h-4 w-4 text-muted-foreground" />,
  not_submitted: <Clock className="h-4 w-4 text-muted-foreground" />,
};

const CITIZENSHIP_LABELS: Record<string, string> = {
  us_citizen: "U.S. Citizen",
  us_national: "U.S. National",
  lawful_permanent_resident: "Lawful Permanent Resident",
  alien_authorized_to_work: "Alien Authorized to Work",
};

const DOC_TYPE_LABELS: Record<string, string> = {
  us_passport: "U.S. Passport",
  passport_card: "Passport Card",
  perm_resident_card: "Permanent Resident Card (I-551)",
  employment_auth_card: "Employment Auth. Card (I-766)",
  foreign_passport_i94: "Foreign Passport + I-94",
  drivers_license: "Driver's License",
  state_id: "State ID",
  school_id: "School ID",
  social_security_card: "Social Security Card",
  birth_certificate: "Birth Certificate",
  us_citizen_id: "U.S. Citizen ID Card",
};

const EVERIFY_LABELS: Record<string, string> = {
  not_submitted: "Not Submitted",
  pending: "Pending",
  authorized: "Authorized",
  tentative_nonconfirmation: "Tentative Non-Confirmation",
  case_in_continuance: "Case in Continuance",
  final_nonconfirmation: "Final Non-Confirmation",
  manual_review: "Manual Review",
};

export const WorkerComplianceTab = ({
  vendorId, vendorName, workerId, companyId,
  w9OnFile, w9Date, agreementSigned, is1099Eligible, paidYTD = 0, auditLog = [],
}: Props) => {
  const { data: i9, isLoading: i9Loading } = useWorkerI9(vendorId);
  const { data: everifyCases = [] } = useEVerifyCases(vendorId);
  const [importOpen, setImportOpen] = useState(false);
  const latestEVerify = everifyCases[0];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2">
          <Shield className="h-4 w-4" /> Compliance — {vendorName}
        </h3>
        {!i9 && !i9Loading && (
          <Button size="sm" onClick={() => setImportOpen(true)}>
            <Plus className="h-4 w-4 mr-1" /> Import I-9
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* I-9 STATUS */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              {STATUS_ICON[i9?.i9_status || "incomplete"]}
              I-9 Status
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            {i9 ? (
              <>
                <Badge variant={i9.i9_status === "complete" ? "default" : "secondary"} className="text-[10px]">
                  {i9.i9_status === "complete" ? "✅ Complete" : i9.i9_status.replace(/_/g, " ")}
                </Badge>
                {i9.section2_verified_at && (
                  <p className="text-muted-foreground">Verified: {format(new Date(i9.section2_verified_at), "MMM d, yyyy")}</p>
                )}
                {i9.section2_verified_by_name && (
                  <p className="text-muted-foreground">By: {i9.section2_verified_by_name}</p>
                )}
                {i9.is_manual_import && (
                  <p className="text-muted-foreground">Source: {i9.manual_import_source?.replace(/_/g, " ")} (manual import)</p>
                )}
                <p className="text-muted-foreground">Citizenship: {CITIZENSHIP_LABELS[i9.citizenship_status] || i9.citizenship_status}</p>
                {i9.ssn_last4 && <p className="text-muted-foreground">SSN: ***-**-{i9.ssn_last4}</p>}
                <Separator className="my-2" />
                <p className="text-xs font-medium">Documents on File:</p>
                {i9.document_list_type === "list_a" && i9.list_a_document_type && (
                  <p className="text-muted-foreground flex items-center gap-1">
                    <FileText className="h-3 w-3" />
                    {DOC_TYPE_LABELS[i9.list_a_document_type] || i9.list_a_document_type}
                    {i9.list_a_document_number && ` — #${i9.list_a_document_number}`}
                    {i9.list_a_expiry_date && ` — exp ${format(new Date(i9.list_a_expiry_date), "MMM yyyy")}`}
                  </p>
                )}
                {i9.document_list_type === "list_b_c" && (
                  <>
                    {i9.list_b_document_type && (
                      <p className="text-muted-foreground flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        B: {DOC_TYPE_LABELS[i9.list_b_document_type] || i9.list_b_document_type}
                        {i9.list_b_document_number && ` — #${i9.list_b_document_number}`}
                      </p>
                    )}
                    {i9.list_c_document_type && (
                      <p className="text-muted-foreground flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        C: {DOC_TYPE_LABELS[i9.list_c_document_type] || i9.list_c_document_type}
                        {i9.list_c_document_number && ` — #${i9.list_c_document_number}`}
                      </p>
                    )}
                  </>
                )}
                <Button variant="outline" size="sm" className="mt-2" onClick={() => setImportOpen(true)}>
                  View / Edit I-9
                </Button>
              </>
            ) : (
              <div className="text-muted-foreground space-y-2">
                <p>❌ No I-9 on file</p>
                <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}>
                  <Plus className="h-3 w-3 mr-1" /> Import I-9
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* E-VERIFY */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              {STATUS_ICON[latestEVerify?.case_status || "not_submitted"]}
              E-Verify
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            {latestEVerify ? (
              <>
                <Badge variant={latestEVerify.case_status === "authorized" ? "default" : "secondary"} className="text-[10px]">
                  {latestEVerify.case_status === "authorized" ? "✅" : "⏳"} {EVERIFY_LABELS[latestEVerify.case_status] || latestEVerify.case_status}
                </Badge>
                {latestEVerify.case_number && <p className="text-muted-foreground">Case #: {latestEVerify.case_number}</p>}
                {latestEVerify.submitted_at && <p className="text-muted-foreground">Submitted: {format(new Date(latestEVerify.submitted_at), "MMM d, yyyy")}</p>}
              </>
            ) : (
              <p className="text-muted-foreground">○ Not submitted</p>
            )}
          </CardContent>
        </Card>

        {/* W9 / TAX */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              {w9OnFile ? <CheckCircle2 className="h-4 w-4 text-primary" /> : <AlertTriangle className="h-4 w-4 text-destructive" />}
              W9 / Tax
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            <p>{w9OnFile ? "✅ W9 on file" : "❌ W9 missing"}{w9Date && ` — ${format(new Date(w9Date), "MMM d, yyyy")}`}</p>
            {i9?.ssn_last4 && <p className="text-muted-foreground">SSN on file: ***-**-{i9.ssn_last4}</p>}
            <p className="text-muted-foreground">1099 {is1099Eligible ? `Required: ✅ (YTD: $${paidYTD.toLocaleString()})` : "Not eligible"}</p>
            {!w9OnFile && paidYTD > 600 && (
              <div className="rounded-md bg-destructive/10 border border-destructive/30 p-2 text-xs text-destructive mt-2">
                ⚠️ YTD payments exceed $600 — W9 required immediately for 1099 compliance
              </div>
            )}
          </CardContent>
        </Card>

        {/* IC AGREEMENT */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              {agreementSigned ? <CheckCircle2 className="h-4 w-4 text-primary" /> : <AlertTriangle className="h-4 w-4 text-warning" />}
              IC Agreement
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <p>{agreementSigned ? `✅ Signed — ${format(new Date(agreementSigned), "MMM d, yyyy")}` : "❌ Not signed"}</p>
          </CardContent>
        </Card>
      </div>

      {/* COMPLIANCE HISTORY */}
      {auditLog.length > 0 && (
        <>
          <Separator />
          <div>
            <h4 className="text-sm font-semibold mb-2">Compliance History</h4>
            <div className="space-y-2">
              {auditLog.slice(0, 10).map((entry, i) => (
                <div key={i} className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="w-24 shrink-0">{entry.created_at ? format(new Date(entry.created_at), "MMM d, yyyy") : "—"}</span>
                  <span className="font-medium text-foreground">{entry.field_name?.replace(/_/g, " ")}</span>
                  <span>{entry.change_reason || "—"}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <ManualI9ImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        vendorId={vendorId}
        vendorName={vendorName}
        workerId={workerId}
        companyId={companyId}
      />
    </div>
  );
};
